import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';

import { DialogService } from '@blaze-shared/shared-feature';
import { themesSelectors } from '@blaze-shared/theming-data-access';
import { type AppTheme } from '@blaze-shared/theming-domain';

@UntilDestroy()
@Component({
  selector: 'blaze-theme-editor-panel',
  templateUrl: './theme-editor-panel.component.html',
  styleUrls: ['./theme-editor-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideInOut', [
      state(
        'false',
        style({
          height: 0,
        }),
      ),
      state(
        'true',
        style({
          height: '*',
        }),
      ),
      transition('false => true', [animate('250ms ease-in')]),
      transition('true => false', [animate('250ms ease-in')]),
    ]),
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('250ms 250ms ease-in', style({ opacity: 1 })),
      ]),
      transition(':leave', [style({ opacity: 0 })]),
    ]),
  ],
  standalone: false,
})
export class ThemeEditorPanelComponent implements OnInit {
  private readonly dialogService = inject(DialogService);
  private readonly store = inject(Store);

  @Input() theme!: AppTheme;
  @Input() selected = false;
  @Input() applied = false;
  @Input() modified = false;
  @Input() showDeleteButton = false;
  @Input() showResetButton = false;
  @Input() projectId = '';

  @Output() selectionChange = new EventEmitter();
  @Output() delete = new EventEmitter<string>();
  @Output() resetTheme = new EventEmitter<string>();

  private currentThemeId: string | undefined;
  private allThemes: Dictionary<AppTheme> = {};

  ngOnInit(): void {
    this.store
      .select(themesSelectors.getThemeEntities)
      .pipe(untilDestroyed(this))
      .subscribe((themes) => {
        this.allThemes = themes;
      });
    this.store
      .select(themesSelectors.getAppliedTheme)
      .pipe(untilDestroyed(this))
      .subscribe((theme) => {
        this.currentThemeId = theme?.id;
      });
  }

  onHeaderClick(): void {
    this.selectionChange.emit();
  }

  onDeleteClick(): void {
    let details = '';
    if (this.currentThemeId === this.theme.id) {
      details =
        '<br /><br />This theme is used by the current project. Deleting it may affect the appearance and layout of the project.';
    } else if (
      (this.allThemes[this.theme.id]?.projectIds ?? []).filter(
        (projectId) => projectId !== this.projectId,
      ).length > 0
    ) {
      details =
        '<br /><br />This theme is currently used by other projects in the project group. Deleting it may affect the appearance and layout of those projects.';
    }

    this.dialogService
      .openConfirmDialog({
        title: `Delete theme`,
        confirmLabel: 'Delete',
        description: `Are you sure you want to delete ${this.theme.name}? ${details}`,
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.delete.emit(this.theme.id);
        }
      });
  }

  onResetClick(): void {
    this.dialogService
      .openConfirmDialog(
        {
          title: 'Reset Theme',
          confirmLabel: 'Reset',
          cancelLabel: 'Cancel',
          description: 'Are you sure you want to reset the selected theme to original?',
        },
        {
          autoFocus: 'dialog',
        },
      )
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.resetTheme.emit(this.theme.id);
        }
      });
  }
}
